import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Input, Button, Typography, Alert, Space, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, LinkOutlined } from '@ant-design/icons';
import api from '../../services/api';

const { Text, Paragraph } = Typography;

const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';

// Modal pra escolher o slug do jogador. Bloqueante (choice-once): quando abre,
// so fecha depois de reservar. Ou o user clica em "escolher depois" e fica de
// fora ate o proximo prompt.
export default function SlugChooserModal({ open, onClose, onDone, canSkip = true }) {
  const [slug, setSlug] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const [check, setCheck] = useState({ status: 'idle', errors: [] }); // idle | checking | available | taken
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get('/community/players/suggest-slug').then(r => {
      if (r.data.suggestion) {
        setSuggestion(r.data.suggestion);
        if (!slug) setSlug(r.data.suggestion);
      }
    }).catch(() => {});
  // eslint-disable-next-line
  }, [open]);

  // Debounced validation
  useEffect(() => {
    if (!slug || slug.length < 3) { setCheck({ status: 'idle', errors: [] }); return; }
    setCheck({ status: 'checking', errors: [] });
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/community/players/check-slug/${encodeURIComponent(slug)}`);
        if (r.data.available) setCheck({ status: 'available', errors: [] });
        else setCheck({ status: 'taken', errors: r.data.errors || ['Já em uso'] });
      } catch { setCheck({ status: 'taken', errors: ['Erro ao validar'] }); }
    }, 400);
    return () => clearTimeout(t);
  }, [slug]);

  const handleSubmit = () => {
    if (check.status !== 'available') return;
    setConfirmOpen(true);
  };

  const confirmReserve = async () => {
    setSaving(true);
    try {
      const r = await api.post('/community/players/me/slug', { slug });
      message.success('Perfeito! Seu perfil já tá no ar');
      onDone?.(r.data.slug);
      setConfirmOpen(false);
      onClose?.();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Erro ao reservar');
    } finally { setSaving(false); }
  };

  const urlPreview = `comunidade.futezap.com.br/jogador/${slug || 'seu-nome'}`;

  return (
    <>
      <Modal
        open={open}
        onCancel={canSkip ? onClose : undefined}
        maskClosable={false}
        closable={canSkip}
        footer={null}
        width={500}
        styles={{ body: { padding: 0 } }}
      >
        {/* Header verde */}
        <div style={{
          background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 100%)`,
          color: '#fff', padding: '28px 24px', borderRadius: '8px 8px 0 0',
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{
            position: 'absolute', top: -60, right: -60, width: 200, height: 200,
            borderRadius: '50%', background: '#fff', opacity: 0.1,
          }} />
          <div style={{ position: 'relative', fontSize: 30, marginBottom: 8 }}>🔗</div>
          <div style={{ position: 'relative', fontSize: 22, fontWeight: 900, letterSpacing: -0.5 }}>
            Escolha o link do teu perfil
          </div>
          <div style={{ position: 'relative', fontSize: 13, opacity: 0.9, marginTop: 6, lineHeight: 1.5 }}>
            Agora você tem perfil pessoal público e compartilhável.<br/>
            Escolhe o nome antes que os melhores acabem!
          </div>
        </div>

        <div style={{ padding: 24 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
            padding: '10px 12px', background: '#f5f7fa', borderRadius: 10, border: '1px solid #e8ecef',
          }}>
            <LinkOutlined style={{ color: BRAND_DARK }} />
            <span style={{ color: '#5a6a63', fontSize: 13, fontFamily: 'monospace' }}>
              {urlPreview}
            </span>
          </div>

          <Input
            size="large"
            value={slug}
            onChange={e => setSlug(e.target.value.toLowerCase())}
            placeholder="seu-nome"
            maxLength={20}
            style={{ borderRadius: 10, height: 48 }}
            suffix={
              check.status === 'checking' ? <LoadingOutlined /> :
              check.status === 'available' ? <CheckCircleOutlined style={{ color: BRAND }} /> :
              check.status === 'taken' ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> :
              null
            }
          />

          {check.status === 'taken' && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#ff4d4f' }}>
              {check.errors.join(' · ')}
            </div>
          )}

          {check.status === 'available' && (
            <div style={{ marginTop: 8, fontSize: 12, color: BRAND_DARK, fontWeight: 600 }}>
              ✓ Disponível!
            </div>
          )}

          {suggestion && slug !== suggestion && (
            <div style={{ marginTop: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Sugestão: </Text>
              <a onClick={() => setSlug(suggestion)} style={{ fontSize: 12, color: BRAND_DARK, fontWeight: 600 }}>
                {suggestion}
              </a>
            </div>
          )}

          <Alert type="warning" showIcon style={{ marginTop: 20 }}
            message="Escolha única"
            description="Depois de escolher, não dá pra trocar. Escolhe com carinho — é a URL que você vai compartilhar." />

          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 12, color: '#8a9a93', marginBottom: 8 }}>Regras:</div>
            <Space direction="vertical" size={2} style={{ fontSize: 12, color: '#5a6a63' }}>
              <div>· 3 a 20 caracteres</div>
              <div>· Letras minúsculas, números e hífen</div>
              <div>· Sem palavrão, ofensa ou nome político</div>
            </Space>
          </div>

          <Space style={{ marginTop: 24, width: '100%', justifyContent: 'space-between' }}>
            {canSkip && (
              <Button onClick={onClose} style={{ height: 44 }}>
                Escolher depois
              </Button>
            )}
            <Button
              type="primary" size="large"
              disabled={check.status !== 'available'}
              onClick={handleSubmit}
              style={{
                marginLeft: 'auto',
                height: 44, borderRadius: 10, fontWeight: 800,
                background: BRAND, borderColor: BRAND,
                boxShadow: check.status === 'available' ? `0 8px 20px rgba(37, 211, 102, 0.35)` : 'none',
              }}>
              Reservar meu link
            </Button>
          </Space>
        </div>
      </Modal>

      <Modal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        okText="Sim, reservar"
        cancelText="Voltar"
        onOk={confirmReserve}
        okButtonProps={{ loading: saving, danger: true }}
        title="Confirma?"
      >
        <p>Você vai reservar o link:</p>
        <p style={{ fontFamily: 'monospace', fontSize: 15, background: '#f5f7fa', padding: '8px 12px', borderRadius: 8 }}>
          comunidade.futezap.com.br/jogador/<strong>{slug}</strong>
        </p>
        <p style={{ color: '#ff4d4f' }}>
          <strong>Escolha única.</strong> Não dá pra trocar depois. Confirma?
        </p>
      </Modal>
    </>
  );
}
